import { useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { createContext, useCallback, useMemo, useState } from 'react';

import { ISForm, ISFormCustomField, ISFormInputField } from '@/shared/services/forms/forms.types';

import {
	IFormDetailsPageContext,
	IFormDetailsPageProviderProps,
} from './form-details-page-provider.types';

export const FormDetailsPageContext = createContext<IFormDetailsPageContext>({
	repliedSubmissionsMap: {},
	updatedFormDetails: null,
	updateRepliedSubmissionsMap: () => {},
	refetchFormDetails: () => {},
	startingTimeStamp: '',
	addFormCustomColumns: () => {},
	deleteCustomColumn: () => {},
	updateCustomColumn: () => {},
	updateDefaultArchiveChatStatus: () => {},
	updateFormStatus: () => {},
	updateFormClosingDate: () => {},
	updateQuickReplies: () => {},
	updateFormLabels: () => {},
	updateMultipleSubmissions: () => {},
	updateGetVerifiedApplicantsOnly: () => {},
	handleToggleColumnVisibility: () => {},
	updateReviewMode: () => {},
	updateFormStages: () => {},
	updateFormDetails: () => {},
	refreshFormDetails: () => {},
});

export function FormDetailsPageProvider({
	children,
	queryKey,
	onRefetchQuery,
	startingTimeStamp,
	formDetails,
	notificationSettings,
	onRefreshFormDetails,
}: IFormDetailsPageProviderProps) {
	const queryClient = useQueryClient();

	const [repliedSubmissionsMap, setRepliedSubmissionsMap] = useState<
		IFormDetailsPageContext['repliedSubmissionsMap']
	>({});

	const handleRefetchFormDetails = useCallback<
		IFormDetailsPageContext['refetchFormDetails']
	>(() => {
		onRefetchQuery();
	}, [onRefetchQuery]);

	const handleRefreshFormDetails = useCallback<
		IFormDetailsPageContext['refreshFormDetails']
	>(() => {
		onRefreshFormDetails();
	}, [onRefreshFormDetails]);

	const handleAddFormCustomColumns = useCallback<IFormDetailsPageContext['addFormCustomColumns']>(
		(columns = []) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft: ISForm) => {
					draft.customFields = [
						...(draft.customFields || []),
						...(columns?.map((e) => ({ ...e, visibility: true })) || []),
					];
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleUpdateFormDetails = useCallback<IFormDetailsPageContext['updateFormDetails']>(
		(formDetails: Partial<ISForm>) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft: ISForm) => {
					Object.assign(draft, formDetails);
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleUpdateFormColumn = useCallback<IFormDetailsPageContext['updateCustomColumn']>(
		(columnId: string, customField: Partial<ISFormCustomField>) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft: ISForm) => {
					const columnIdx = draft.customFields?.findIndex((aiField) => aiField.id === columnId);
					if (columnIdx && columnIdx > -1 && draft.customFields) {
						draft.customFields[columnIdx] = {
							...draft.customFields[columnIdx],
							...customField,
						};
					}
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleDeleteFormColumn = useCallback<IFormDetailsPageContext['deleteCustomColumn']>(
		(columnId) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft: ISForm) => {
					draft.customFields = draft.customFields?.filter(
						(customField) => customField.id !== columnId
					);
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleUpdateDefaultArchiveChatStatus = useCallback<
		IFormDetailsPageContext['updateDefaultArchiveChatStatus']
	>(
		(chatStatus) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft) => {
					draft.defaultArchiveChatStatus = chatStatus;
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleUpdateRepliedSubmissionsMap = useCallback<
		IFormDetailsPageContext['updateRepliedSubmissionsMap']
	>(
		(submissionId, updateType) => {
			const repliedSubmissionsMapCopy = { ...repliedSubmissionsMap };

			if (updateType === 'ADD') {
				repliedSubmissionsMapCopy[submissionId] = true;
			}

			if (updateType === 'REMOVE') {
				delete repliedSubmissionsMapCopy[submissionId];
			}

			setRepliedSubmissionsMap(repliedSubmissionsMapCopy);

			const updatedFormDetailsCopy = produce(formDetails, (draft) => {
				draft.formCounts.respondedCount += 1;
			});

			queryClient.setQueryData(queryKey, updatedFormDetailsCopy);
		},
		[repliedSubmissionsMap, queryClient, queryKey, formDetails]
	);

	const handleUpdateFormStatus = useCallback<IFormDetailsPageContext['updateFormStatus']>(
		(status) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft) => {
					draft.status = status;
					draft.closingDate = '';
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleUpdateFormStages = useCallback<IFormDetailsPageContext['updateFormStages']>(
		(stages, fromMessagesPage = false) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm | { form: ISForm }) => {
				return produce(prevData, (draft) => {
					if (fromMessagesPage && 'form' in draft) {
						draft.form.stages = stages;
					} else if ('stages' in draft) {
						draft.stages = stages;
					}
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleUpdateFormClosingDate = useCallback<IFormDetailsPageContext['updateFormClosingDate']>(
		(closingDate) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft) => {
					draft.closingDate = closingDate;
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleUpdateQuickReplies = useCallback<IFormDetailsPageContext['updateQuickReplies']>(
		(quickReplies) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft) => {
					draft.quickReplies = quickReplies;
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleUpdateFormLabels = useCallback<IFormDetailsPageContext['updateFormLabels']>(
		(labels) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft) => {
					draft.labelsWithColor = labels;
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleUpdateMultipleSubmissions = useCallback<
		IFormDetailsPageContext['updateMultipleSubmissions']
	>(
		(isMultipleSubmissionsEnabled) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft) => {
					draft.multipleSubmissions = isMultipleSubmissionsEnabled;
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleUpdateGetVerifiedApplicantsOnly = useCallback<
		IFormDetailsPageContext['updateGetVerifiedApplicantsOnly']
	>(
		(isGetVerifiedApplicantsOnlyEnabled) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft) => {
					draft.emailVerificationRequired = isGetVerifiedApplicantsOnlyEnabled;
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleToggleColumnVisibility = useCallback<
		IFormDetailsPageContext['handleToggleColumnVisibility']
	>(
		(columnId: string, visibility: boolean) => {
			if (!columnId || visibility === undefined) return;

			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft) => {
					draft.inputFields =
						draft?.inputFields?.map((inputField: ISFormInputField) => {
							if (inputField.id === columnId) {
								inputField.visibility = visibility;
							}
							return inputField;
						}) || [];
					draft.customFields =
						draft?.customFields?.map((customField: ISFormCustomField) => {
							if (customField.id === columnId) {
								customField.visibility = visibility;
							}
							return customField;
						}) || [];
				});
			});
		},
		[queryClient, queryKey]
	);

	const handleUpdateReviewMode = useCallback<IFormDetailsPageContext['updateReviewMode']>(
		(mode) => {
			queryClient.setQueryData(queryKey, (prevData: ISForm) => {
				return produce(prevData, (draft) => {
					draft.reviewMode = mode;
					if (mode === 'fast') {
						draft.fastMode = 'NO_CONFIRMATION_WITH_MESSAGE';
					} else if (mode === 'messageLater') {
						draft.fastMode = 'NO_CONFIRMATION_WITHOUT_MESSAGE';
					} else {
						draft.fastMode = 'DEFAULT';
					}
				});
			});
		},
		[queryClient, queryKey]
	);

	const value = useMemo(() => {
		return {
			updatedFormDetails: formDetails,
			notificationSettings,
			repliedSubmissionsMap,
			updateRepliedSubmissionsMap: handleUpdateRepliedSubmissionsMap,
			refetchFormDetails: handleRefetchFormDetails,
			startingTimeStamp,
			addFormCustomColumns: handleAddFormCustomColumns,
			updateDefaultArchiveChatStatus: handleUpdateDefaultArchiveChatStatus,
			updateFormStatus: handleUpdateFormStatus,
			updateFormClosingDate: handleUpdateFormClosingDate,
			updateQuickReplies: handleUpdateQuickReplies,
			updateFormLabels: handleUpdateFormLabels,
			updateMultipleSubmissions: handleUpdateMultipleSubmissions,
			updateGetVerifiedApplicantsOnly: handleUpdateGetVerifiedApplicantsOnly,
			handleToggleColumnVisibility: handleToggleColumnVisibility,
			updateReviewMode: handleUpdateReviewMode,
			updateFormStages: handleUpdateFormStages,
			updateFormDetails: handleUpdateFormDetails,
			deleteCustomColumn: handleDeleteFormColumn,
			updateCustomColumn: handleUpdateFormColumn,
			refreshFormDetails: handleRefreshFormDetails,
		};
	}, [
		formDetails,
		notificationSettings,
		repliedSubmissionsMap,
		handleUpdateRepliedSubmissionsMap,
		handleRefetchFormDetails,
		startingTimeStamp,
		handleAddFormCustomColumns,
		handleDeleteFormColumn,
		handleUpdateDefaultArchiveChatStatus,
		handleUpdateFormStatus,
		handleUpdateFormClosingDate,
		handleUpdateQuickReplies,
		handleUpdateFormLabels,
		handleUpdateMultipleSubmissions,
		handleUpdateGetVerifiedApplicantsOnly,
		handleUpdateFormColumn,
		handleToggleColumnVisibility,
		handleUpdateReviewMode,
		handleUpdateFormStages,
		handleUpdateFormDetails,
		handleRefreshFormDetails,
	]);

	return (
		<FormDetailsPageContext.Provider value={value}>{children}</FormDetailsPageContext.Provider>
	);
}
